import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Conservative 20s timeout. Slow Indian 4G networks frequently take 5–8s for a fresh
// TLS + Cloudflare round-trip on a cold connection, so we can't be too aggressive.
// Without a timeout, axios uses the browser default (often 60–120s) which leaves the
// catalog spinner stuck forever when the backend or an upstream proxy is unhappy.
const DEFAULT_TIMEOUT_MS = 20000;

export const api = axios.create({ baseURL: API, timeout: DEFAULT_TIMEOUT_MS });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sv_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-retry GETs that fail with a network error or a 5xx. Mutations (POST/PATCH/DELETE)
// are NOT retried — they may have side effects and the caller decides how to recover.
// 3 attempts total, exponential backoff (250ms → 750ms → 1.5s) so a transient blip on
// the user's flaky cell network doesn't turn into a "Failed to load catalog" toast.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const config = err?.config;
    const method = (config?.method || "get").toLowerCase();
    const status = err?.response?.status;
    const retriable = (
      method === "get" &&
      config &&
      !config._retryCount &&
      (
        // Network-level failure (no response received) — axios sets err.code='ERR_NETWORK'
        !err.response ||
        // Cloudflare 5xx / origin overload / gateway timeouts
        (typeof status === "number" && status >= 500 && status <= 599) ||
        // Request timed out client-side
        err.code === "ECONNABORTED"
      )
    );
    if (retriable) {
      config._retryCount = (config._retryCount || 0);
      const maxRetries = 2;
      while (config._retryCount < maxRetries) {
        config._retryCount += 1;
        const wait = 250 * Math.pow(3, config._retryCount - 1);
        await new Promise((resolve) => setTimeout(resolve, wait));
        try {
          return await api.request({ ...config, timeout: DEFAULT_TIMEOUT_MS });
        } catch (retryErr) {
          if (config._retryCount >= maxRetries) {
            return Promise.reject(retryErr);
          }
        }
      }
    }
    if (status === 401) {
      // optional: clear and redirect
    }
    return Promise.reject(err);
  }
);

// All catalog images are served from a public route (UUID paths are unguessable).
// Now that watermarking is removed, URLs are byte-identical for every visitor —
// so we deliberately DON'T append the user's JWT here. Stable URLs let browsers
// and any upstream CDN share a single cached entry across all visitors instead
// of caching the same image under a per-token URL.
export const fileUrl = (path) => {
  if (!path) return null;
  return `${API}/files/${path}`;
};

export const fileUrlWithParam = (path, params = {}) => {
  if (!path) return null;
  const search = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v != null) search.set(k, String(v));
  });
  const qs = search.toString();
  return `${API}/files/${path}${qs ? `?${qs}` : ""}`;
};
